<template>
  <div
    component-name="grid-bath-concept"
    class="overflow-hidden grid grid-cols-1 grid-rows-1"
  >
    <ClientOnly>
      <div
        ref="tpl_canvasContainer"
        class="row-start-1 row-end-2 col-start-1 col-end-2 w-full h-full"
        :class="{
          '!opacity-0': resizing || !ww || !wh,
        }"
      ></div>

      <transition-fade duration="quick">
        <div
          v-if="resizing || !ww || !wh"
          class="row-start-1 row-end-2 col-start-1 col-end-2 w-full h-full flex justify-center items-center"
          inert
        >
          <spinner :spinning="true" />
        </div>
      </transition-fade>

      <div
        ref="tpl_wrapper"
        class="isolate fixed top-0 left-0 min-w-min min-h-min invisible"
        inert
      >
        <div
          ref="tpl_grid"
          class="grid"
          :style="{
            width: `${gridWidth}px`,
            minWidth: `${gridWidth}px`,
            height: `${gridHeight}px`,
            minHeight: `${gridHeight}px`,
            gridTemplateColumns: `repeat(${nColumns}, 1fr)`,
          }"
        >
          <div v-for="(image, i) in randomImages" class="relative top-0 left-0">
            <figure
              class="absolute p-0 m-0 js-plane flex justify-center items-center"
              :class="{
                'inset-2xl': mqMode === 'desktop',
                'inset-xl': mqMode === 'mobile',
              }"
              :data-src="fromCloudinary(image.asset.src, { width: 1000 })"
              :data-type="image.type"
              :data-slug="image.slug"
            >
              <img
                :src="fromCloudinary(image.asset.src, { width: 1000 })"
                :alt="image.asset.alt"
                class="w-full h-auto oject-cover"
              />
            </figure>
          </div>
        </div>
      </div>
    </ClientOnly>
  </div>
</template>

<script setup>
import {
  Raycaster,
  Scene,
  OrthographicCamera,
  WebGLRenderer,
  TextureLoader,
  PlaneGeometry,
  ShaderMaterial,
  Object3D,
  Vector2,
  Vector3,
  Mesh,
  Frustum,
  Matrix4,
} from "three";

import { useRootStore } from "@/stores/root";

const props = defineProps({
  interactive: {
    type: Boolean,
    required: false,
    default: false,
  },

  useIntro: {
    type: Boolean,
    required: false,
    default: false,
  },

  gridConfigs: {
    type: Object,
    required: false,
    default: () => ({
      visibleColumns: { desktop: 3.5, mobile: 1.5 },
      cellAspectRatio: 1 / 1,
      nRows: { desktop: 4, mobile: 5 },
    }),
  },

  skipIntro: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const rootStore = useRootStore();
const tpl_wrapper = templateRef("tpl_wrapper");
const tpl_grid = templateRef("tpl_grid");
const tpl_canvasContainer = templateRef("tpl_canvasContainer");
const hoverScale = 1.05;

const { fromCloudinary } = useCdn();
const { isTablet } = useScreen();
const visibility = useDocumentVisibility();

const images = rootStore.projectBathroom.value.projects
  .map((item) => {
    return {
      name: item.name,
      slug: item.slug,
      type: "cover",
      asset: item.cover.asset,
    };
  })
  .concat(
    rootStore.projectBathroom.value.projects.map((item) => {
      return {
        name: item.name,
        slug: item.slug,
        type: "moodboard",
        asset: item.moodboard.asset,
      };
    })
  );

const randomImages = images.sort(() => Math.random() - 0.5);

const { width: ww, height: wh } = useElementBounding(tpl_canvasContainer);

const gridWidth = ref(0);
const gridHeight = ref(0);
const nColumns = ref(0);
const mqMode = ref(ww.value > wh.value ? "desktop" : "mobile");
const resizing = ref(false);
const paused = ref(false);
const wasPlaying = ref(false);
const emit = defineEmits(["introCompleted", "itemClicked"]);

let resizeId = null,
  introTimeline = null,
  core,
  mouseStatus;

let diff = 0;
if (randomImages.length < 20) {
  diff = 20 - randomImages.length;
} else if (randomImages.length % props.gridConfigs.nRows[mqMode.value] !== 0) {
  diff =
    props.gridConfigs.nRows[mqMode.value] -
    (randomImages.length % props.gridConfigs.nRows[mqMode.value]);
}

for (let i = 0; i < diff; i++) {
  const randomIndex = Math.floor(Math.random() * randomImages.length);
  randomImages.push(randomImages[randomIndex]);
}

const resize = () => {
  if (core) {
    core.dispose();
    core = null;
  }

  resizing.value = true;
  clearTimeout(resizeId);
  resizeId = setTimeout(() => {
    resizing.value = false;
    evaluateGridSize();
    if (!core) {
      nextTick(() => {
        core = new Core();
      });
    }
  }, 600);
};

function evaluateGridSize() {
  mqMode.value = ww.value > wh.value ? "desktop" : "mobile";
  const columnWidth = ww.value / props.gridConfigs.visibleColumns[mqMode.value];
  const rowHeight = columnWidth / props.gridConfigs.cellAspectRatio;
  nColumns.value = Math.ceil(
    randomImages.length / props.gridConfigs.nRows[mqMode.value]
  );
  gridWidth.value = parseInt(columnWidth * nColumns.value);
  gridHeight.value = parseInt(
    rowHeight * props.gridConfigs.nRows[mqMode.value]
  );
}

function getBoundingBox2D(plane, camera, renderer) {
  const vertices = plane.geometry.attributes.position;
  const vector = new Vector3();
  const screenCoords = [];

  // Proietta ogni vertice nello spazio dello schermo
  for (let i = 0; i < vertices.count; i++) {
    vector.fromBufferAttribute(vertices, i).applyMatrix4(plane.matrixWorld);
    vector.project(camera); // Proietta nello spazio normalizzato del clip (NDC)

    // Converti in coordinate schermo
    const x =
      (vector.x * 0.5 + 0.5) *
      (renderer.domElement.width / window.devicePixelRatio);
    const y =
      (1 - (vector.y * 0.5 + 0.5)) *
      (renderer.domElement.height / window.devicePixelRatio);

    screenCoords.push({ x, y });
  }

  // Trova i minimi e massimi per calcolare il bounding box
  const minX = Math.min(...screenCoords.map((p) => p.x));
  const minY = Math.min(...screenCoords.map((p) => p.y));
  const maxX = Math.max(...screenCoords.map((p) => p.x));
  const maxY = Math.max(...screenCoords.map((p) => p.y));

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

/** CORE **/
class Core {
  constructor() {
    this.tx = 0;
    this.ty = 0;
    this.cx = 0;
    this.cy = 0;

    this.requestFrameId = null;

    this.diff = 0;

    this.planes = [];

    this.wheel = { x: 0, y: 0 };
    this.on = { x: 0, y: 0 };
    this.max = { x: 0, y: 0 };

    this.isDragging = false;
    this.reycaster = new Raycaster();

    const mouseMultiplier = 0.6;
    const firefoxMultiplier = 20;

    this.isFirefox = navigator.userAgent.indexOf("Firefox") > -1;
    this.isWindows = navigator.appVersion.indexOf("Win") != -1;

    this.multipliers = {
      mouse: this.isWindows ? mouseMultiplier * 2 : mouseMultiplier,
      firefox: this.isFirefox ? firefoxMultiplier * 2 : firefoxMultiplier,
    };

    this.mouseDown = { time: 0, x: 0, y: 0 };

    this.el = tpl_grid.value;

    /** GL specifics **/
    this.scene = new Scene();

    this.camera = new OrthographicCamera(
      ww.value / -2,
      ww.value / 2,
      wh.value / 2,
      wh.value / -2,
      1,
      1000
    );
    this.camera.lookAt(this.scene.position);
    this.camera.position.z = 1;

    this.renderer = new WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    this.renderer.setSize(ww.value, wh.value);
    this.renderer.setPixelRatio(window.devicePixelRatio);

    tpl_canvasContainer.value.appendChild(this.renderer.domElement);
    /** Gl specifics end **/

    this.addPlanes();
    this.resize();

    if (props.interactive) {
      this.addEvents();
    }

    this.renderer.render(this.scene, this.camera);

    nextTick(() => {
      props.useIntro ? this.introAnimation() : this.animateToGrid();
    });
  }

  getPlanesInFrustum(plane3d) {
    this.camera.updateMatrixWorld();
    const frustum = new Frustum();
    frustum.setFromProjectionMatrix(
      new Matrix4().multiplyMatrices(
        this.camera.projectionMatrix,
        this.camera.matrixWorldInverse
      )
    );
    return this.planes.filter((plane) => {
      return frustum.intersectsObject(plane.mesh);
    });
  }

  introAnimation() {
    // this.tx = -784.89;
    // this.ty = 6034.96;

    // this.tick();

    if (rootStore.bathroomIntroAnimation) {
      this.autoplay();
      gsap.ticker.add(this.tick);
      return;
    }

    rootStore.setBathroomIntroAnimation(true);
    this.tick();

    introTimeline = gsap.timeline({
      onUpdate: () => {
        this.renderer.render(this.scene, this.camera);
      },
      onComplete: () => {
        emit("introCompleted");
        this.animateToGrid();
      },
    });

    const planesInFrustum = this.getPlanesInFrustum();
    planesInFrustum.forEach((plane, index) => {
      plane.userData = {
        x: plane.position.x,
        y: plane.position.y,
      };

      introTimeline.fromTo(
        plane.position,
        { y: -this.max.y },
        {
          // y: plane.position.y,
          x: 0,
          y: 0,
          duration: 1.5,
          ease: "expo.inOut",
        },
        "<0.125"
      );
    });
  }

  animateToGrid() {
    // Timeline 2: Parte dopo la fine di Timeline 1

    introTimeline = gsap.timeline({
      onUpdate: () => {
        this.renderer.render(this.scene, this.camera);
      },
      onComplete: () => {
        setTimeout(() => {
          if (props.skipIntro) {
            this.ty += 0.5;
            this.tick();
          } else {
            this.autoplay();
            gsap.ticker.add(this.tick);
          }
        }, 100);
        introTimeline = null;
      },
    });

    const planesInFrustum = this.getPlanesInFrustum();
    planesInFrustum.forEach((plane, index) => {
      introTimeline.to(
        plane.position,
        {
          x: plane.userData.x,
          y: plane.userData.y,
          duration: props.skipIntro ? 0 : 1.4,
          ease: "expo.inOut",
        },
        0.2 * Math.random()
      );
    });
  }

  autoplay() {
    if (!this.isDragging) {
      /* this.tx -= 0.5;
      this.ty -= 0.1; */
      this.ty += 0.5;
    }

    this.requestFrameId = requestAnimationFrame(() => this.autoplay());
  }

  addEvents() {
    this.renderer.domElement.style.touchAction = "none";
    this.renderer.domElement.addEventListener("pointermove", this.onMouseMove);
    this.renderer.domElement.addEventListener("pointerdown", this.onMouseDown);
    this.renderer.domElement.addEventListener("pointerup", this.onMouseUp);
    this.renderer.domElement.addEventListener(
      "pointerleave",
      this.onMouseLeave
    );
    this.renderer.domElement.addEventListener("wheel", this.onWheel);
  }

  removeEvents() {
    this.renderer.domElement.style.touchAction = "";

    this.renderer.domElement.removeEventListener(
      "pointermove",
      this.onMouseMove
    );
    this.renderer.domElement.removeEventListener(
      "pointerdown",
      this.onMouseDown
    );
    this.renderer.domElement.removeEventListener("pointerup", this.onMouseUp);
    this.renderer.domElement.removeEventListener(
      "pointerleave",
      this.onMouseLeave
    );
    this.renderer.domElement.removeEventListener("wheel", this.onWheel);
  }

  addPlanes() {
    const planes = [...document.querySelectorAll(".js-plane")];
    this.planes = planes.map((el, i) => {
      const plane = new Plane();
      plane.init(el, i);
      this.scene.add(plane);
      planes.push(plane);
      return plane;
    });
  }

  tick = () => {
    const xDiff = this.tx - this.cx;
    const yDiff = this.ty - this.cy;

    this.cx += xDiff * 0.09;
    this.cx = Math.round(this.cx * 100) / 100;

    this.cy += yDiff * 0.09;
    this.cy = Math.round(this.cy * 100) / 100;

    this.diff = Math.max(Math.abs(yDiff * 0.0001), Math.abs(xDiff * 0.0001));

    this.planes.length &&
      this.planes.forEach((plane) => {
        plane.update(this.cx, this.cy, this.max, this.diff);
      });

    this.renderer.render(this.scene, this.camera);
  };

  onMouseMove = ({ clientX, clientY }) => {
    mouseStatus = "inside";

    // detect intersection
    this.reycaster.setFromCamera(
      { x: (clientX / ww.value) * 2 - 1, y: -(clientY / wh.value) * 2 + 1 },
      this.camera
    );

    if (isTablet.value) {
      const intersects = this.reycaster.intersectObjects(this.scene.children);
      if (intersects.length) {
        const { object } = intersects[0];
        object.setHover(true);
        this.renderer.domElement.style.cursor = "pointer";
      } else {
        this.renderer.domElement.style.cursor = "";
        this.planes.forEach((plane) => {
          plane.setHover(false);
        });
      }
    }

    if (!this.isDragging) {
      return;
    }

    this.tx = this.on.x + clientX * 2.5;
    this.ty = this.on.y - clientY * 2.5;
  };

  onMouseDown = ({ clientX, clientY }) => {
    if (this.isDragging) return;

    this.isDragging = true;

    this.on.x = this.tx - clientX * 2.5;
    this.on.y = this.ty + clientY * 2.5;

    this.mouseDown = { time: Date.now(), x: clientX, y: clientY };
  };

  onMouseClick = (event) => {
    // trigger mouse move event to prevent a bug
    // when the user click without moving the mouse
    this.onMouseMove(event);

    const intersects = this.reycaster.intersectObjects(this.scene.children);

    if (intersects.length) {
      const { object } = intersects[0];
      this.pause();

      function onComplete(object, boundingBox) {
        // add event once on window
        window.addEventListener(
          "coreography-start",
          () => {
            object.material.uniforms.u_opacity.value = 0;
            this.renderer.render(this.scene, this.camera);
          },
          { once: true }
        );

        window.addEventListener(
          "coreography-complete",
          () => {
            object.material.uniforms.u_opacity.value = 1;
            object.material.uniforms.clip_size.value = hoverScale;
            this.renderer.render(this.scene, this.camera);
          },
          { once: true }
        );

        emit("itemClicked", {
          slug: object.el.dataset.slug,
          type: object.el.dataset.type,
          src: object.el.dataset.src,
          boundingBox,
        });
      }

      function onUpdate() {
        this.renderer.render(this.scene, this.camera);
      }

      if (isTablet.value) {
        const boundingBox = getBoundingBox2D(
          object,
          this.camera,
          this.renderer
        );

        if (object.hoverTween.progress() == 1) {
          onComplete.bind(this, object, boundingBox)();
          return;
        }

        object.onHoverUpdate = onUpdate.bind(this);
        object.onHoverComplete = onComplete.bind(this, object, boundingBox);
      } else {
        emit("itemClicked", {
          slug: object.el.dataset.slug,
          type: object.el.dataset.type,
        });
      }
    }
  };

  onMouseUp = ({ clientX, clientY }) => {
    if (!this.isDragging) return;

    this.isDragging = false;

    const delta =
      Math.abs(this.mouseDown.x - clientX) +
      Math.abs(this.mouseDown.y - clientY);

    if ((Date.now() - this.mouseDown.time < 200 && delta < 10) || delta < 5) {
      this.onMouseClick({ clientX, clientY });
    }
  };

  onMouseLeave = () => {
    mouseStatus = "outside";

    this.isDragging = false;
    this.planes.forEach((plane) => {
      plane.setHover(false);
    });
  };

  onWheel = (e) => {
    const { mouse, firefox } = this.multipliers;

    this.wheel.x = e.wheelDeltaX || e.deltaX * -1;
    this.wheel.y = e.wheelDeltaY || e.deltaY * -1;

    if (this.isFirefox && e.deltaMode === 1) {
      this.wheel.x *= firefox;
      this.wheel.y *= firefox;
    }

    this.wheel.y *= mouse;
    this.wheel.x *= mouse;

    if (Math.abs(this.wheel.x) > 600) {
      this.wheel.x *= 0.1;
    }

    if (Math.abs(this.wheel.y) > 600) {
      this.wheel.y *= 0.1;
    }

    this.tx += this.wheel.x;
    this.ty -= this.wheel.y;
  };

  resize = () => {
    evaluateGridSize();

    const { bottom, right } = this.el.getBoundingClientRect();

    this.max.x = right;
    this.max.y = bottom;
  };

  dispose() {
    gsap.ticker.remove(this.tick);

    this.planes.forEach((plane) => {
      plane.dispose();
    });

    this.mouseDown = null;
    this.renderer.domElement.remove();
    this.removeEvents();
  }

  pause() {
    paused.value = true;
    this.removeEvents();
    cancelAnimationFrame(this.requestFrameId);
    gsap.ticker.remove(this.tick);
  }

  play() {
    // loop through planes and set opacity to 1
    paused.value = false;
    wasPlaying.value = true;
    this.addEvents();
    this.planes.forEach((plane) => {
      plane.mesh.material.uniforms.u_opacity.value = 1;
      plane.mesh.material.uniforms.clip_size.value = hoverScale;
    });
    this.renderer.render(this.scene, this.camera);
    gsap.ticker.add(this.tick);
    this.autoplay();
  }
}

/** PLANE **/
const loader = new TextureLoader();

const vertexShader = `
precision mediump float;

uniform float u_diff;
uniform float u_diff_overrides;

varying vec2 vUv;

void main(){
  vec3 pos = position;

  // override diff
  if(u_diff_overrides > 0.){
    pos.y *= 1. - u_diff_overrides;
    pos.x *= 1. - u_diff_overrides;
  } else {
    pos.y *= 1. - u_diff;
    pos.x *= 1. - u_diff;
  }

  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.);;
}
`;

const fragmentShader = `
precision mediump float;

uniform vec2 u_res;
uniform vec2 u_size;
uniform float u_opacity;
uniform float clip_size;
uniform sampler2D u_texture;

vec2 cover(vec2 screenSize, vec2 imageSize, vec2 uv) {
    float screenRatio = screenSize.x / screenSize.y;
    float imageRatio = imageSize.x / imageSize.y;

    vec2 newSize = screenRatio < imageRatio
        ? vec2(imageSize.x * (screenSize.y / imageSize.y), screenSize.y)
        : vec2(screenSize.x, imageSize.y * (screenSize.x / imageSize.x));

    vec2 newOffset = (screenRatio < imageRatio
        ? vec2((newSize.x - screenSize.x) / 2.0, 0.0)
        : vec2(0.0, (newSize.y - screenSize.y) / 2.0)) / newSize;

    return uv * screenSize / newSize + newOffset;
}

varying vec2 vUv;

void main() {
    vec2 uv = vUv;

    // Calcola UV per effetto cover
    vec2 uvCover = cover(u_res, u_size, uv);

    // Applica il crop con clip_size
    float scale = clip_size;
    vec2 cropOffset = (vec2(1.0) - vec2(1.0) / scale) * 0.5;
    vec2 uvCropped = uvCover * (1.0 / scale) + cropOffset;

    vec4 texture = texture2D(u_texture, uvCropped);
    gl_FragColor = vec4(texture.rgb, texture.a * u_opacity);
}

`;

const geometry = new PlaneGeometry(1, 1, 1, 1);
const material = new ShaderMaterial({
  fragmentShader,
  vertexShader,
  transparent: true,
});

class Plane extends Object3D {
  init(el, i) {
    this.el = el;

    this.x = 0;
    this.y = 0;

    this.my = 1;
    // this.my = 1 - (i % nColumns.value) * 0.1;

    this.geometry = geometry;
    this.material = material.clone();

    this.material.uniforms = {
      u_texture: { value: 0 },
      u_res: { value: new Vector2(1, 1) },
      u_size: { value: new Vector2(1, 1) },
      u_diff: { value: 0 },
      u_diff_overrides: { value: 0 },
      clip_size: { value: hoverScale },
      u_opacity: { value: 1 },
    };

    this.texture = loader.load(this.el.dataset.src, (texture) => {
      const { naturalWidth, naturalHeight } = texture.image;
      const { u_size, u_texture } = this.material.uniforms;
      u_texture.value = texture;
      u_size.value.x = naturalWidth;
      u_size.value.y = naturalHeight;
    });

    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.setHover = this.setHover.bind(this);
    this.mesh.el = this.el;
    this.add(this.mesh);

    this.userData = {
      hover: false,
    };

    this.resize();
  }

  update = (x, y, max, diff) => {
    const { right, bottom } = this.rect;
    const { u_diff } = this.material.uniforms;
    this.y =
      gsap.utils.wrap(-(max.y - bottom), bottom, y * this.my) - this.yOffset;
    this.x = gsap.utils.wrap(-(max.x - right), right, x) - this.xOffset;
    u_diff.value = diff;
    this.position.x = this.x;
    this.position.y = this.y;

    if (!isTablet.value) {
      return;
    }

    const intersects = core.reycaster.intersectObject(this.mesh);
    if (intersects.length) {
      if (
        !this.userData.hover &&
        props.interactive &&
        mouseStatus == "inside"
      ) {
        this.setHover(true);
      }
    } else {
      if (this.userData.hover) {
        this.setHover(false);
      }
    }
  };

  resize() {
    this.rect = this.el.querySelector("img").getBoundingClientRect();

    const { left, top, width, height } = this.rect;
    const { u_res } = this.material.uniforms;

    this.xOffset = left + width / 2 - ww.value / 2;
    this.yOffset = top + height / 2 - wh.value / 2;

    this.position.x = this.xOffset;
    this.position.y = this.yOffset;

    u_res.value.x = width;
    u_res.value.y = height;

    this.mesh.scale.set(width, height, 1);
  }

  setHover(hover) {
    if (hover === this.userData.hover) {
      return;
    }

    const { clip_size } = this.material.uniforms;

    this.userData.hover = hover;

    const duration = 1.2;
    const ease = "power1.out";

    // duration: hover ? 0.9 : 0.7,
    // ease: hover ? "power2.out" : "sine.out",

    if (this.mesh.hoverTween) {
      this.mesh.hoverTween.kill();
    }

    this.mesh.hoverTween = gsap.to(clip_size, {
      value: hover ? 1 : hoverScale,
      duration: hover ? 0.9 : 0.7,
      ease: hover ? "power2.out" : "sine.out",
      onUpdate: () => {
        if (hover && this.mesh.onHoverUpdate) {
          this.mesh.onHoverUpdate();
        }
      },
      onComplete: () => {
        if (hover && this.mesh.onHoverComplete) {
          this.mesh.onHoverComplete();
          this.mesh.onHoverComplete = null;
          this.mesh.onHoverUpdate = null;
        }
      },
    });
  }

  dispose() {
    this.texture.dispose();
    this.material.dispose();
    this.geometry.dispose();
  }
}

watch(
  () => props.interactive,
  (newVal) => {
    if (!core) return;

    newVal ? core.addEvents() : core.removeEvents();
  }
);

watch(visibility, (value) => {
  if (!core) return;

  if (introTimeline) {
    value == "visible" ? introTimeline.play() : introTimeline.pause();
  } else {
    if (value == "visible") {
      if (wasPlaying.value) {
        core.play();
      }
    } else {
      wasPlaying.value = !paused.value;
      core.pause();
    }
  }
});

defineExpose({
  play: () => core.play(),
  pause: () => core.pause(),
});

useSafeMountedEl([tpl_wrapper, tpl_grid, tpl_canvasContainer], () => {
  evaluateGridSize();
  const jsPlanes = document.querySelectorAll(".js-plane");

  let loaded = 0;
  jsPlanes.forEach((figure) => {
    const img = figure.querySelector("img");

    if (img.complete) {
      loaded++;
      checkLoaded();
    } else {
      img.addEventListener("load", () => {
        loaded++;
        checkLoaded();
      });
    }
  });

  function checkLoaded() {
    if (loaded === jsPlanes.length) {
      nextTick(() => {
        core = new Core();
      });
    }
  }

  window.addEventListener("resize", resize);
});

onUnmounted(() => {
  window.removeEventListener("resize", resize);
});
</script>
